import * as React from 'react'

import {
  ICardListGroupConfig,
  ICardListState,
} from '~/client/src/shared/components/CardList/types'
import type { IOptionItem } from '~/client/src/shared/components/OptionsList/types'
import SortOrder from '~/client/src/shared/enums/SortOrder'

import {
  IListSelectorItem,
  IListSelectorSearchConfig,
  IListSelectorSortConfig,
  SelectionMode,
} from './types'

export interface IListSelectorContextValue<T extends IListSelectorItem> {
  selectionMode: SelectionMode
  searchKey: string
  setSearchKey: (key: string) => void
  sortKey: string
  sortOrder: SortOrder
  groupingKey: string | null
  groupConfig: ICardListGroupConfig<T> | undefined
  listState: ICardListState<T>
  processedItems: T[]
  effectiveIds: Set<string>
  effectiveToggle: ((id: string) => void) | undefined
  effectiveToggleAll: ((ids: string[], selectAll: boolean) => void) | undefined
  draftIds: Set<string>
  /** The list has a pickable item (after search/filter). */
  hasItems: boolean
  /** Every pickable listed item is selected. */
  areAllSelected: boolean
  /** At least one pickable listed item is selected. */
  isAnySelected: boolean
  selectedCount: number
  handleToggleAll: () => void
  handleGroupChange: (key: string | null) => void
  handleSortChange: (key: string | null) => void
  handleSortOrderChange: (order: SortOrder) => void
  initialSortKey: string
  initialSortOrder: SortOrder
  initialGroupingKey: string | null
  searchConfig: IListSelectorSearchConfig<T> | undefined
  sortConfig: IListSelectorSortConfig<T> | undefined
  groupingOptions: IOptionItem[] | undefined
  isItemDisabled: ((item: T) => boolean) | undefined
  portalSelector?: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ListSelectorContext =
  React.createContext<IListSelectorContextValue<any> | null>(null)

export function useListSelectorContext<
  T extends IListSelectorItem,
>(): IListSelectorContextValue<T> {
  const ctx = React.useContext(ListSelectorContext)
  if (!ctx) {
    throw new Error(
      'ListSelector compound components must be used within <ListSelector.Root>',
    )
  }
  return ctx as IListSelectorContextValue<T>
}

export default ListSelectorContext
